import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { statsApi } from '../lib/api.js'
import { parseContactEmail } from '../lib/format.js'
import { useI18n } from '../lib/i18n.jsx'
import { Container, ExternalLink, Logo } from '../ui/index.js'
import { ADMIN_PATHS, PUBLIC_NAV } from './routes.js'
import { useSite } from './useSiteData.js'

/**
 * Site footer.
 *
 * Contact essentials, the same navigation the header carries, and the legal
 * line. Nothing here is decorative: a research group's footer is where a
 * prospective student looks for an email address and where a visitor from
 * mainland China expects to find the ICP filing.
 *
 * Every block is conditional on real data. A group that has not filled in a
 * phone number gets a footer without a phone row, not a row reading "—".
 *
 * 页脚。
 *
 * 联系方式要点、与页头同一份导航，以及备案信息。这里没有装饰性内容：课题组页脚
 * 是有意报考的学生找邮箱的地方，也是国内访客预期看到 ICP 备案号的地方。
 *
 * 每个板块都以真实数据为条件。没填电话的课题组得到的是「没有电话那一行」的页脚，
 * 而不是一行写着「—」。
 *
 * @returns {JSX.Element} Footer / 页脚。
 */
/**
 * Count this visit and read back the running total.
 *
 * Fires once when the footer mounts — which is once per full page load, not per
 * client-side navigation, because the footer is part of the shell and React
 * keeps it mounted while routes change. That is the behaviour we want: a person
 * reading six pages is one visit, and the server would dedupe it anyway.
 *
 * A failure is silent. A visit counter that cannot reach the server is not
 * something to interrupt a reader about; the number simply does not appear.
 *
 * 计入本次访问并读回累计总数。
 *
 * 在页脚挂载时触发一次——也就是每次完整页面加载一次，而不是每次前端路由跳转一次，
 * 因为页脚属于外壳，路由切换期间 React 一直保持它挂载。这正是想要的行为：
 * 一个人读六个页面算一次访问，何况服务端本来也会去重。
 *
 * 失败时保持沉默。一个连不上服务器的访问计数器，不值得去打扰读者；
 * 那个数字不出现就是了。
 *
 * @returns {number|null} Total visits, or null before it is known / 累计访问量，未知时为 null。
 */
function useVisitTotal() {
  const [total, setTotal] = useState(null)

  useEffect(() => {
    let cancelled = false
    statsApi
      .visit()
      .then((payload) => {
        if (!cancelled && typeof payload?.total === 'number') setTotal(payload.total)
      })
      .catch(() => {
        // Silent by design — see the docblock.
        // 刻意静默——见上方说明。
      })
    return () => {
      cancelled = true
    }
  }, [])

  return total
}

export default function SiteFooter() {
  const { t, pick } = useI18n()
  const { data: site } = useSite()

  const groupName = site ? pick(site.groupName) : ''
  // Same opt-in as the contact page: the setting decides, not the layout.
  // 与联系页同一个开关：由设置决定，而不是由版式决定。
  const showAffiliation = site?.showAffiliation !== false
  const institution = showAffiliation && site ? pick(site.institution) : ''
  const department = showAffiliation && site ? pick(site.department) : ''
  const address = site ? pick(site.address) : ''
  const mail = parseContactEmail(site?.email)
  const visits = useVisitTotal()

  return (
    <footer className="site-footer">
      <Container className="site-footer__inner">
        <div className="site-footer__identity">
          {/* Mark and name share a line: they are one thing, and stacking them
              reads as two unrelated items sitting on top of each other.
              标识与组名同处一行：它们本来就是一个整体，上下堆叠会读成
              两个互不相干的东西叠在一起。 */}
          <div className="site-footer__brand">
            <Logo size="md" className="site-footer__logo" />
            <p className="site-footer__name">{groupName}</p>
          </div>

          {institution && <p className="site-footer__affiliation">{institution}</p>}
          {department && <p className="site-footer__affiliation">{department}</p>}

          {/* Last in the block, below the affiliation when one is shown. The
              counter is the least important line here — it belongs after who
              the group is, not wedged between the name and the institution.
              Rendered only once a number is actually known: a counter showing a
              placeholder, or a zero, while it loads is worse than one that
              appears a moment late.
              放在这一块的最后；显示了机构信息时排在其下。计数器是这里最不重要的一行——
              它该排在「这个组是谁」之后，而不是卡在组名和单位中间。
              只有真的拿到数字才渲染：加载期间先显示占位符或 0 的计数器，
              比晚一瞬间出现的计数器更糟。 */}
          {visits !== null && (
            <p className="site-footer__visits">{t('footer.visits', { n: visits.toLocaleString() })}</p>
          )}
        </div>

        <nav className="site-footer__column site-footer__column--links" aria-label={t('footer.links')}>
          <p className="site-footer__heading">{t('footer.links')}</p>
          {/* The links get their own grid rather than inheriting the column's
              single stack: seven items in one vertical line makes the footer
              taller than the content it sits under.
              链接单独用一个栅格，而不是继承外层的单列堆叠：七个条目竖排一列，
              会让页脚比它所承托的内容还高。 */}
          <div className="site-footer__links">
            {PUBLIC_NAV.map((item) => (
              <Link key={item.path} className="site-footer__link" to={item.path}>
                {t(item.key)}
              </Link>
            ))}
          </div>
        </nav>

        <div className="site-footer__column">
          <p className="site-footer__heading">{t('footer.contact')}</p>
          {address && <p className="site-footer__address">{address}</p>}
          {mail.label &&
            (mail.address ? (
              <a className="site-footer__link" href={`mailto:${mail.address}`}>
                {mail.label}
              </a>
            ) : (
              <p className="site-footer__address">{mail.label}</p>
            ))}
          {site?.phone && <p className="site-footer__phone">{site.phone}</p>}
        </div>

        <div className="site-footer__column">
          <p className="site-footer__heading">{t('common.more')}</p>
          {site?.github && (
            <ExternalLink className="site-footer__link" href={site.github}>
              {t('members.github')}
            </ExternalLink>
          )}
          {site?.scholar && (
            <ExternalLink className="site-footer__link" href={site.scholar}>
              {t('members.scholar')}
            </ExternalLink>
          )}
          <Link className="site-footer__link" to={ADMIN_PATHS.dashboard}>
            {t('nav.admin')}
          </Link>
        </div>
      </Container>

      <Container className="site-footer__legal">
        <p className="site-footer__copyright">
          {t('footer.copyright', { year: new Date().getFullYear(), name: groupName })}
        </p>
        <p className="site-footer__note">{t('footer.builtNote')}</p>
        {site?.icp && <p className="site-footer__icp">{site.icp}</p>}
      </Container>
    </footer>
  )
}
